import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  MapPin,
  Recycle,
  CheckCircle2,
  PiggyBank,
  Award,
  Boxes,
  Sparkles,
  Layers,
  TrendingUp,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city, products = [] }) => {
  const recoStandard = products.find(
    (p) => p.slug === "carton-standard-reconditionne",
  );
  const recoLivres = products.find(
    (p) => p.slug === "carton-livres-reconditionne",
  );

  return (
    <>
      {/* ======= SECTION 2 — Livraison directe ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Charte de transport direct
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Vos cartons livrés à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Épargnez-vous la corvée de transporter des piles de colis
              volumineux et instables dans l&apos;habitacle de votre
              voiture. Nous prenons en charge la manutention et le
              transport de vos commandes jusqu&apos;à votre domicile.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Circuit court garanti",
                body: `Aucune plateforme de messagerie externe ne touche à vos produits. Nos propres collaborateurs gèrent le chargement et la dépose à ${city.name}.`,
                Icon: Truck,
              },
              {
                title: "Exclusivité francilienne",
                body: "En limitant notre rayon d'action à l'Île-de-France, nous conservons une maîtrise totale des délais et veillons à ce que votre matériel arrive parfaitement sec et intact.",
                Icon: MapPin,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Reconditionné -40% : strip hero horizontal ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Recycle className="h-3.5 w-3.5" strokeWidth={2.6} />
              L&apos;opportunité maline
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Cartons reconditionnés à −40&nbsp;%
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pourquoi payer le prix fort pour un produit qui ne servira
              qu&apos;une fois&nbsp;? Nous bousculons le marché en vous
              proposant nos cartons reconditionnés de première catégorie.
            </p>
          </div>

          {/* Hero strip with massive -40% */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-3xl overflow-hidden border border-[#cfdbe6]">
            {/* Left massive number */}
            <div className="lg:col-span-4 relative bg-gradient-to-br from-[#0f1d2b] to-[#1a2d3f] text-white p-8 md:p-10 flex flex-col justify-between min-h-[260px]">
              <div
                aria-hidden="true"
                className="absolute -top-16 -right-16 h-52 w-52 rounded-full bg-[#FF7A00]/25 blur-3xl"
              />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00]">
                  Économie immédiate
                </div>
                <div className="mt-4 font-display font-extrabold text-7xl md:text-8xl tracking-tighter text-white leading-[0.9]">
                  −40<span className="text-5xl md:text-6xl">%</span>
                </div>
              </div>
              <div className="relative text-[13px] text-white/70">
                vs prix neuf
              </div>
            </div>

            {/* Right 3 benefits in a compact row */}
            <div className="lg:col-span-8 bg-[#EEF3F7] p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 h-full">
                {[
                  {
                    title: "Sélection drastique",
                    body: "Ces boîtes n'ont connu qu'un seul cycle d'utilisation. Triées à la main, vérifiées sous toutes les coutures et certifiées sans perte de rigidité.",
                    Icon: CheckCircle2,
                  },
                  {
                    title: "Économie massive",
                    body: "Vous bénéficiez d'une décote immédiate de 40% par rapport au tarif du neuf, idéale pour réduire le budget de vos préparatifs.",
                    Icon: PiggyBank,
                  },
                  {
                    title: "Impact circulaire",
                    body: "Donner une seconde vie à ces emballages permet de prolonger l'utilité de la matière première et de réduire les déchets à la source.",
                    Icon: Recycle,
                  },
                ].map(({ title, body, Icon }) => (
                  <div
                    key={title}
                    className="bg-white rounded-2xl border border-[#cfdbe6] p-4 md:p-5 flex flex-col"
                  >
                    <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white mb-3">
                      <Icon className="h-5 w-5" strokeWidth={2.4} />
                    </span>
                    <h3 className="font-display font-extrabold text-[14.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[13px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 2 featured reconditionned products as compact chips */}
          {(recoStandard || recoLivres) && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="text-[12px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                En stock
              </div>
              {[recoStandard, recoLivres].filter(Boolean).map((p) => (
                <Link
                  key={p.id}
                  to={`/produit/${p.slug}`}
                  className="group inline-flex items-center gap-3 h-12 px-5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:shadow-[0_12px_28px_-14px_rgba(35,124,181,0.4)] transition-all"
                  data-testid={`city-chaville-cta-${p.slug}`}
                >
                  <span className="flex items-center justify-center h-7 w-7 rounded-full bg-[#237cb5] text-white">
                    <Recycle className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                  <span className="font-bold text-[13.5px] text-[#0f1d2b]">
                    {p.name}
                  </span>
                  <span className="font-display font-extrabold text-[14px] text-[#237cb5]">
                    {Number(p.price).toFixed(2).replace(".", ",")} €
                  </span>
                  <ArrowRight
                    className="h-3.5 w-3.5 text-[#237cb5] group-hover:translate-x-0.5 transition-transform"
                    strokeWidth={2.6}
                  />
                </Link>
              ))}
              <Link
                to="/cartons-reconditionnes"
                className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] hover:gap-2.5 transition-all ml-1"
                data-testid="city-chaville-cta-all-reco"
              >
                Voir toute la gamme
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Pourquoi commander : 3 facts en cards avec top bar ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
              Faits concrets
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Pourquoi commander chez nous&nbsp;?
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Plutôt que de simples promesses, nous basons notre service
              sur des faits concrets pour sécuriser votre projet.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                kicker: "Une armature",
                title: "Un carton qui ne plie pas",
                body: "Nos produits possèdent une épaisseur de carton supérieure aux standards des grandes surfaces, offrant un rempart efficace contre l'écrasement lors du gerbage dans le camion.",
                Icon: Award,
                accent: "#237cb5",
              },
              {
                kicker: "Zéro rupture",
                title: "Disponibilité immédiate",
                body: "Nous stockons nos volumes en masse pour vous garantir une disponibilité immédiate de vos kits, même lors de la haute saison des déménagements.",
                Icon: TrendingUp,
                accent: "#FF7A00",
              },
              {
                kicker: "Flexibilité",
                title: "Tous les formats couverts",
                body: "Du petit carton à archives jusqu'aux formats spécifiques pour la verrerie, nous couvrons l'intégralité du spectre logistique pour qu'aucun objet ne se retrouve sans protection adaptée.",
                Icon: Layers,
                accent: "#237cb5",
              },
            ].map(({ kicker, title, body, Icon, accent }) => (
              <article
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] overflow-hidden flex flex-col"
              >
                {/* Top accent bar */}
                <div
                  className="h-1.5 w-full"
                  style={{ backgroundColor: accent }}
                />
                <div className="p-5 md:p-6 flex-1">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="flex items-center justify-center h-11 w-11 rounded-full text-white"
                      style={{ backgroundColor: accent }}
                    >
                      <Icon className="h-5 w-5" strokeWidth={2.4} />
                    </span>
                    <div
                      className="text-[11px] uppercase tracking-[0.16em] font-bold"
                      style={{ color: accent }}
                    >
                      {kicker}
                    </div>
                  </div>
                  <h3 className="mt-4 font-display font-extrabold text-[16.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/cartons-demenagement"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-chaville-cta-shop"
            >
              <Boxes className="h-4 w-4" strokeWidth={2.6} />
              Voir tous les cartons
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/packs-cartons"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="city-chaville-cta-packs"
            >
              Voir les kits complets
            </Link>
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-chaville-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
